import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import React from 'react';

import * as GQL from '~/graphql/generated/client';

interface Filter {
  name: string;
  selected: boolean;
}

export interface Identifier {
  id: string;
  name: string;
  identifier: string;
  version: string;
}

export interface PolicyContextInfo {
  policy: Identifier;
  bundles: Identifier[];
}

interface SelectedControlBundlesPayload {
  policyId: string;
  controlBundles: Identifier[];
}

type State = Readonly<{
  /**
   * A list of policies available to add to the project
   * This is a filtered list excluding policies previously added to the project
   *
   */
  policies: GQL.PolicyFragment[];
  /**
   * A list of search keywords)
   */
  searchKeywords: string[];
  /**
   * A list of filters
   */
  filters: Filter[];
  /**
   * A list of selected policies (i.e added to "shopping cart")
   */
  selectedPolicies: PolicyContextInfo[];
  /**
   * The policy to configure (i.e. bundle selection)
   */
  configurePolicyId: string | null;
  /**
   * Show confirmation dialog
   */
  showConfirmationDialog: boolean;
}>;

const initialState: State = {
  policies: [],
  searchKeywords: [],
  filters: [],
  selectedPolicies: [],
  configurePolicyId: null,
  showConfirmationDialog: false,
};

const leftNavSlice = createSlice({
  name: 'projectPolicyLeftNav',
  initialState,
  reducers: {
    /**
     * Set available policies
     */
    setPolicies: (state, { payload }: PayloadAction<GQL.PolicyFragment[]>) => {
      state.policies = payload;
    },
    /**
     * Set search keywords
     */
    setSearchKeywords: (state, { payload }: PayloadAction<string[]>) => {
      state.searchKeywords = payload;
    },
    /**
     * set filter
     */
    setFilter: (
      state,
      { payload: { name, selected } }: PayloadAction<Filter>
    ) => {
      const filterIndex = state.filters.findIndex(
        (filter) => filter.name === name
      );
      if (filterIndex > -1) {
        state.filters[filterIndex].selected = selected;
      } else {
        state.filters.push({ name, selected });
      }
    },
    /**
     * clear all filters
     */
    clearFilters: (state) => {
      state.filters = [];
    },
    /**
     * Add policy to list of selected policies (i.e. "shopping cart")
     */
    addSelectedPolicy: (
      state,
      { payload }: PayloadAction<PolicyContextInfo>
    ) => {
      state.selectedPolicies.push(payload);
    },
    /**
     * Remove policy from list of selected policies (i.e. "shopping cart")
     */
    removeSelectedPolicy: (state, { payload }: PayloadAction<string>) => {
      const policyIndex = state.selectedPolicies.findIndex(
        (policyInfo) => policyInfo.policy.id === payload
      );
      if (policyIndex > -1) {
        state.selectedPolicies.splice(policyIndex, 1);
      }
    },
    /**
     * Set control bundle list for policy
     * This is a subset of the policy's control bundles
     */
    setControlBundlesForPolicy: (
      state,
      {
        payload: { policyId, controlBundles },
      }: PayloadAction<SelectedControlBundlesPayload>
    ) => {
      const policyIndex = state.selectedPolicies.findIndex(
        (policyInfo) => policyInfo.policy.id === policyId
      );
      if (policyIndex > -1) {
        state.selectedPolicies[policyIndex].bundles = controlBundles;
      }
    },
    /**
     * Set the policy to configure (i.e. bundle selection)
     */
    setConfigurePolicyId: (
      state,
      { payload }: PayloadAction<string | null>
    ) => {
      state.configurePolicyId = payload;
    },
    /**
     * Show confirmation dialog (i.e. ready to checkout)
     */
    setShowConfirmationDialog: (state, { payload }: PayloadAction<boolean>) => {
      state.showConfirmationDialog = payload;
    },
    /**
     * reset state
     */
    reset: (state) => {
      state.searchKeywords = [];
      state.filters = [];
      state.selectedPolicies = [];
      state.configurePolicyId = null;
    },
  },
});

// Helper type to replace the return type of action creator
type ReplaceReturnType<T extends (...a: any) => any, TNewReturn> = (
  ...a: Parameters<T>
) => TNewReturn;

const { actions } = leftNavSlice;

interface ContextT {
  state: State;
  // The following actions are safe to be used from appContext directly
  setPolicies: ReplaceReturnType<typeof actions.setPolicies, void>;
  setSearchKeywords: ReplaceReturnType<typeof actions.setSearchKeywords, void>;
  setFilter: ReplaceReturnType<typeof actions.setFilter, void>;
  clearFilters: ReplaceReturnType<typeof actions.clearFilters, void>;
  addSelectedPolicy: ReplaceReturnType<typeof actions.addSelectedPolicy, void>;
  removeSelectedPolicy: ReplaceReturnType<
    typeof actions.removeSelectedPolicy,
    void
  >;
  setControlBundlesForPolicy: ReplaceReturnType<
    typeof actions.setControlBundlesForPolicy,
    void
  >;
  setConfigurePolicyId: ReplaceReturnType<
    typeof actions.setConfigurePolicyId,
    void
  >;
  setShowConfirmationDialog: ReplaceReturnType<
    typeof actions.setShowConfirmationDialog,
    void
  >;
  reset: ReplaceReturnType<typeof actions.reset, void>;
}

// @ts-expect-error: intentionally leaving default value to be empty
const Context = React.createContext<ContextT>();

interface Props {
  children: React.ReactNode;
}

export function ProjectPolicyLeftNavContextProvider(props: Props) {
  const [state, dispatch] = React.useReducer(
    leftNavSlice.reducer,
    initialState
  );

  const actionList: Omit<ContextT, 'state'> = {
    setPolicies: (p) => dispatch(actions.setPolicies(p)),
    setSearchKeywords: (k) => dispatch(actions.setSearchKeywords(k)),
    setFilter: (f) => dispatch(actions.setFilter(f)),
    clearFilters: () => dispatch(actions.clearFilters()),
    addSelectedPolicy: (p) => dispatch(actions.addSelectedPolicy(p)),
    removeSelectedPolicy: (p) => dispatch(actions.removeSelectedPolicy(p)),
    setControlBundlesForPolicy: (b) =>
      dispatch(actions.setControlBundlesForPolicy(b)),
    setConfigurePolicyId: (p) => dispatch(actions.setConfigurePolicyId(p)),
    setShowConfirmationDialog: (s) =>
      dispatch(actions.setShowConfirmationDialog(s)),
    reset: () => dispatch(actions.reset()),
  };

  return <Context.Provider value={{ state, ...actionList }} {...props} />;
}

export default function useProjectPolicyLeftNavContext() {
  const context = React.useContext(Context);
  if (!context) {
    throw new Error(
      `useProjectPolicyLeftNavContext must be used within a LeftNavContextProvider`
    );
  }
  return context;
}
